import React from 'react';
import styled from 'styled-components';
import { Link } from '../components/Button';
import {
    BUY_PAGE_FONTS_ID,
    BUY_PAGE_LICENCES_ID,
    VIEWPORT,
} from '../settings/Global';
import { HeadingMedium } from '../components/Heading';
import LicenceSelector from '../components/LicenceSelector';
import FontSelector from '../components/FontSelector';
import InlineUnderlinedLink, { A } from '../components/InlineUnderlinedLink';
import { useFontFamilyGroup } from '../components/PageContext';
import useCartQuery from '../hooks/useCartQuery';
import {
    createCheckoutUrl,
    createContactUrl,
    createFaqUrl,
} from '../utils/urlHelper';
import useMaintenanceModeOverlay from '../hooks/useMaintenanceModeOverlay';
import CartSummaryMobile from '../components/CartSummaryMobile';
import useIsDefaultColorScheme from '../hooks/useIsDefaultColorScheme';
import {
    type CheckoutButtonProps,
    checkoutButtonStyles,
} from '../components/CheckoutFormElements';
import { TREES_THAT_COUNT_HYPERLINK } from '../settings/Hyperlinks';

const HORN_EMOJI = ['🤘', '🤘🏻', '🤘🏼', '🤘🏽', '🤘🏾', '🤘🏿'];

export { Head } from '../components/Head';

const CheckoutLink = styled(Link)<CheckoutButtonProps>`
    ${checkoutButtonStyles};

    @media not all and (max-width: ${VIEWPORT.TABLET}px) {
        display: none;
    }
`;

const Container = styled.div`
    padding: var(--paddingPageMedium);
    margin: 0 var(--gridMarginGap);
    display: grid;
    grid-template-columns: var(--gridTemplateColumnsDefault);
    grid-column-gap: var(--gridColumnGap);

    --spacingUnderTitle: var(--spacing6);
    --spacingSections: var(--spacing8);

    @media screen and (max-width: ${VIEWPORT.TABLET}px) {
        padding-bottom: 0;
    }
`;

const Inner = styled.div`
    grid-column: 2 / span 6;
    display: grid;
    grid-template-columns: 100%;
    grid-row-gap: var(--spacingSections);

    @media screen and (max-width: ${VIEWPORT.TABLET_LARGE}px) {
        grid-column: 1 / span 7;
    }

    @media screen and (max-width: ${VIEWPORT.TABLET}px) {
        grid-column: 1 / -1;
    }
`;

const NumberedHeading = styled(HeadingMedium)`
    scroll-margin-top: var(--navbarHeightCollapsed);
    margin-bottom: var(--spacingUnderTitle);
`;

const Subtitle = styled.p`
    margin-top: var(--spacingUnderTitle);
`;

const LoadingMessage = styled.p`
    color: var(--foregroundColorMix3);
`;

function BuyPage(): React.ReactElement {
    const fontFamilyGroup = useFontFamilyGroup();
    const { name } = fontFamilyGroup;
    const cartQuery = useCartQuery();
    const isDefaultTheme = useIsDefaultColorScheme();
    const cart = cartQuery?.data?.cart;
    const hornEmoji = React.useMemo(() => {
        // Randomise horn emoji skin colour
        return HORN_EMOJI[Math.floor(Math.random() * HORN_EMOJI.length)];
    }, []);
    useMaintenanceModeOverlay();

    return (
        <Container>
            <Inner>
                <div>
                    <HeadingMedium as="h1">{`Buy ${name}`}</HeadingMedium>
                    <Subtitle>
                        {hornEmoji}&nbsp;&nbsp;Pay once, use forever.
                        <br />
                        🚫&nbsp;&nbsp;No monthly subscriptions.
                        <br />
                        🎁&nbsp;&nbsp;Package discounts apply automatically.
                        <br />
                        🤔&nbsp;&nbsp;Read the{' '}
                        <InlineUnderlinedLink to={createFaqUrl()}>
                            FAQs
                        </InlineUnderlinedLink>{' '}
                        or{' '}
                        <InlineUnderlinedLink to={createContactUrl()}>
                            contact us
                        </InlineUnderlinedLink>{' '}
                        to upgrade previous orders.
                        {fontFamilyGroup.slug === 'manuka' ? (
                            <>
                                <br />
                                <br />
                                🌳&nbsp;&nbsp;We donate 20% of Mānuka sales to{' '}
                                <A href={TREES_THAT_COUNT_HYPERLINK.url}>
                                    {TREES_THAT_COUNT_HYPERLINK.title}
                                </A>
                                .
                            </>
                        ) : null}
                    </Subtitle>
                </div>
                {cart ? (
                    <>
                        <div>
                            <NumberedHeading id={BUY_PAGE_LICENCES_ID} as="h2">
                                1. Select licences
                            </NumberedHeading>
                            <LicenceSelector />
                        </div>
                        <div>
                            <NumberedHeading id={BUY_PAGE_FONTS_ID} as="h2">
                                2. Select fonts
                            </NumberedHeading>
                            <FontSelector />
                        </div>
                        <CartSummaryMobile
                            buttonElement={
                                <CheckoutLink
                                    $isDefaultTheme={isDefaultTheme}
                                    to={createCheckoutUrl()}
                                >
                                    Checkout
                                </CheckoutLink>
                            }
                            isBuyPage
                        />
                    </>
                ) : (
                    <div>
                        <NumberedHeading as="h2">
                            1. Select licences
                        </NumberedHeading>
                        <LoadingMessage>Loading, please wait…</LoadingMessage>
                    </div>
                )}
            </Inner>
        </Container>
    );
}

export default React.memo(BuyPage);
